import { UserState } from '../states/user.state';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  updateOwnClinicLocationProviderData,
  updateOwnUserInfo,
  userInfo,
} from '../thunks/user.thunk';
import * as firebase from 'firebase/app';
import { UserInfo } from '../../common/model/dto/user-info';
import { AxiosError } from 'axios';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { ClinicLocationProvider } from '../../common/model/dto/clinic-location-provider';
import { FullScriptOauthAccessToken } from '../../common/model/dto/fullscript/fullscript-oauth-access-token';
import {
  managerRolePostfix,
  viewerRolePostfix,
} from '../../common/utils/auth.helpers';

const initialState: UserState = {
  currentUser: null,
  isLoading: false,
  errorMessage: '',
  selectedRole: '',
  selectedClinicLocation: undefined,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    setCurrentUser: (state, action: PayloadAction<UserInfo>) => {
      _setCurrentUser(state, action);
    },
    setSelectedRole: (state, action: PayloadAction<string>) => {
      state.selectedRole = action.payload;
    },
    setSelectedClinicLocation: (
      state,
      action: PayloadAction<ClinicLocation>
    ) => {
      state.selectedClinicLocation = action.payload;
    },
    setSelectedClinicRole: (state, action: PayloadAction<string>) => {
      state.selectedClinicRole = action.payload;
    },
    updateFullScriptUserInfo: (
      state,
      action: PayloadAction<FullScriptOauthAccessToken | undefined>
    ) => {
      if (state.currentUser) {
        state.currentUser.fullscript = action.payload
          ? {
              oauth: action.payload,
            }
          : action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // @ts-ignore please leave the ts ignore for now for store and state related config files
    builder
      .addCase(userInfo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(userInfo.fulfilled, (state, action: PayloadAction<UserInfo>) => {
        _setCurrentUser(state, action);
      })
      .addCase(
        userInfo.rejected,
        (state, action: PayloadAction<AxiosError>) => {
          state.errorMessage = action.payload.message;
          state.isLoading = false;
        }
      );

    // @ts-ignore please leave the ts ignore for now for store and state related config files
    builder
      .addCase(updateOwnUserInfo.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        updateOwnUserInfo.fulfilled,
        (state, action: PayloadAction<UserInfo>) => {
          state.currentUser = {
            ...state.currentUser,
            ...action.payload,
          };
          state.isLoading = false;
        }
      )
      .addCase(
        updateOwnUserInfo.rejected,
        (state, action: PayloadAction<firebase.FirebaseError>) => {
          state.errorMessage = action.payload.message;
          state.isLoading = false;
        }
      );

    // @ts-ignore please leave the ts ignore for now for store and state related config files
    builder
      .addCase(updateOwnClinicLocationProviderData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        updateOwnClinicLocationProviderData.fulfilled,
        (
          state,
          action: PayloadAction<{
            clinicLocationProviderId: string;
            updatedProperties: any;
          }>
        ) => {
          if (state.currentUser?.provider_clinic_locations) {
            state.currentUser.provider_clinic_locations =
              state.currentUser.provider_clinic_locations.map(
                (clinicLocationProvider) => {
                  if (
                    clinicLocationProvider.id ===
                    action.payload.clinicLocationProviderId
                  ) {
                    return {
                      ...clinicLocationProvider,
                      ...action.payload.updatedProperties,
                    };
                  }

                  return clinicLocationProvider;
                }
              );
          }

          state.isLoading = false;
        }
      )
      .addCase(
        updateOwnClinicLocationProviderData.rejected,
        (state, action: PayloadAction<firebase.FirebaseError>) => {
          state.errorMessage = action.payload.message;
          state.isLoading = false;
        }
      );
  },
});

export const {
  clearCurrentUser,
  setSelectedRole,
  setSelectedClinicLocation,
  setSelectedClinicRole,
  updateFullScriptUserInfo,
  setCurrentUser,
} = userSlice.actions;
export default userSlice.reducer;

function _setCurrentUser(state: any, action: PayloadAction<UserInfo>): void {
  state.currentUser = action.payload;
  let previouslySelectedClinicLocationId =
    action.payload.selected_clinic_location_id;
  const userClinicLocations = [
    ...action.payload.provider_clinic_locations,
    ...action.payload.patient_clinic_locations,
  ] as unknown as ClinicLocationProvider[];

  state.selectedRole = action.payload.selected_role ?? action.payload?.roles[0];

  if (previouslySelectedClinicLocationId) {
    const selectedRelation = userClinicLocations.find(
      (userClinicLocation) =>
        userClinicLocation.clinic_location.id ===
        previouslySelectedClinicLocationId
    );
    state.selectedClinicLocation = selectedRelation?.clinic_location;
    state.selectedClinicRole = selectedRelation?.clinic_role ?? 'Patient';
  } else if (userClinicLocations.length > 0) {
    state.selectedClinicLocation = userClinicLocations[0].clinic_location;
    state.selectedClinicRole = userClinicLocations[0].clinic_role;
  }

  const managerRoles = action.payload?.roles.filter((role) =>
    role.endsWith(managerRolePostfix)
  );

  if (
    !state.selectedRole &&
    !state.selectedClinicLocation &&
    !state.selectedClinicRole &&
    managerRoles.length > 0
  ) {
    state.selectedRole = managerRoles[0];
  }

  const viewerRoles = action.payload?.roles.filter((role) =>
    role.endsWith(viewerRolePostfix)
  );

  if (
    !state.selectedRole &&
    !state.selectedClinicLocation &&
    !state.selectedClinicRole &&
    viewerRoles.length > 0
  ) {
    state.selectedRole = viewerRoles[0];
  }

  state.isLoading = false;
}
